import { useEffect, useRef, useState } from 'react';

import {
    Box,
    Button,
    ButtonGroup,
    ButtonProps,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalHeader,
    ModalOverlay,
    Text,
    useDisclosure,
    UseModalProps
} from '@chakra-ui/react';
import { ThemingProps } from '@chakra-ui/system';
import { FaUser } from 'react-icons/fa';
import { FormattedMessage } from 'react-intl';

import { EventType } from '~/components/Event';
import {
    RegistrationTypeEnum,
    useMicrositeConfig
} from '~/components/Site/provider/MicrositeConfigProvider';
import UserPasswordResetForm from '~/components/User/UserPasswordResetForm';
import { useFirebaseApp } from '~/lib/firebase';
import { getAuthorizationUrl, shouldAuthenticateWithOAuth } from '~/lib/oauth/oauth-v2';

import UserLoginForm from './UserLoginForm';
import UserRegisterForm from './UserRegisterForm';

type OnboardingModalFormType = 'login' | 'registration' | 'password_reset';

// TODO: Remove as is it not used on FOMF site
const UserOnboardingModal: React.FunctionComponent<
    {
        event: EventType | undefined;
        initialForm?: OnboardingModalFormType;
        children?: React.ReactNode;
    } & UseModalProps &
        ThemingProps
> = ({ event, initialForm = 'login', onClose, ...props }) => {
    const [formKey, setFormKey] = useState(initialForm);
    const { registration } = useMicrositeConfig();
    const initialFocusRef = useRef<HTMLInputElement>(null);

    const customOnClose = () => {
        setFormKey(initialForm);
        onClose();
    };

    return (
        <>
            <Modal
                initialFocusRef={initialFocusRef}
                onClose={customOnClose}
                size={formKey === 'registration' ? '2xl' : 'md'}
                {...props}
            >
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>
                        {formKey === 'login' ? (
                            <FormattedMessage
                                id="user.onboarding.modal.header.login"
                                defaultMessage="Mit Zugangsdaten anmelden"
                            />
                        ) : null}
                        {formKey === 'registration' ? (
                            <FormattedMessage
                                id="user.onboarding.modal.header.register"
                                defaultMessage="Kostenlos registrieren"
                            />
                        ) : null}
                        {formKey === 'password_reset' ? (
                            <FormattedMessage
                                id="user.onboarding.modal.header.password_reset"
                                defaultMessage="Passwort zurücksetzen"
                            />
                        ) : null}
                        {formKey === 'login' && UserRegisterForm.getEmailAlreadyExists() ? (
                            <Text fontSize="sm" color="red.500">
                                <FormattedMessage
                                    id="user.onboarding.modal.register_error.email_already_in_use"
                                    defaultMessage="Die E-Mail {email} ist bereits registriert. Bitte geben Sie das zugehörige Passwort ein."
                                    values={{
                                        email: UserRegisterForm.getEmailAlreadyExists()
                                    }}
                                />
                            </Text>
                        ) : null}
                        {formKey !== 'login' && UserLoginForm.getEmailUserNotFound() ? (
                            <Text fontSize="sm" color="red.500">
                                <FormattedMessage
                                    id="user.onboarding.modal.login_error.email_not_found"
                                    defaultMessage="Sie sind noch nicht registriert, {br} bitte geben Sie Ihre Daten ein."
                                />
                            </Text>
                        ) : null}
                    </ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        {formKey === 'login' ? (
                            <>
                                <UserLoginForm
                                    initialFocusRef={initialFocusRef}
                                    openPasswordResetForm={() => setFormKey('password_reset')}
                                />
                                <Box textAlign="center" mt="4" mb="4">
                                    {registration.type === RegistrationTypeEnum.Normal ? (
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            colorScheme="brand"
                                            onClick={() => setFormKey('registration')}
                                        >
                                            <FormattedMessage
                                                id="user.onboarding.login_form.register_link.text"
                                                defaultMessage="Kostenlos registrieren"
                                            />
                                        </Button>
                                    ) : null}
                                </Box>
                            </>
                        ) : null}
                        {formKey === 'registration' ? (
                            <UserRegisterForm
                                event={event}
                                initialFocusRef={initialFocusRef}
                                onLogin={() => setFormKey('login')}
                            />
                        ) : null}
                        {formKey === 'password_reset' ? (
                            <UserPasswordResetForm initialFocusRef={initialFocusRef} />
                        ) : null}
                    </ModalBody>
                </ModalContent>
            </Modal>
        </>
    );
};

const UserOnboarding: React.FunctionComponent<{ event?: EventType } & ButtonProps> = ({
    event,
    ...props
}) => {
    const { userId, userIsLoading } = useFirebaseApp();
    const { isOpen, onClose, onOpen } = useDisclosure();
    const [formKey, setFormKey] = useState<OnboardingModalFormType>();
    const { registration } = useMicrositeConfig();

    useEffect(() => {
        UserRegisterForm.removeEmailAlreadyExists();
    }, []);

    if (userId && !userIsLoading) {
        return null;
    }

    // Special handling for "Sonder-Academy on Health Care Policy" event
    if (event?.id === '4e582d99-b084-415e-9710-f25171b0d8cc') {
        return null;
    }

    // Special handling for "Inselspital Bern - 16. Berner Ultraschall- und Perinatalkongress" event
    // @todo: remove this, once the transition to V2 OAuth is ready
    if (event?.id === 'cc113f97-68d3-4497-b78a-f950391aee93') {
        return null;
    }

    const onModalClose = () => {
        onClose();
        setFormKey(undefined);
    };

    const openLoginForm = () => {
        if (shouldAuthenticateWithOAuth()) {
            window.location.href = getAuthorizationUrl();
        } else if (event?.ext_login_url) {
            window.location.href = event?.ext_login_url;
        } else {
            setFormKey('login');
            onOpen();
        }
    };

    const openRegisterForm = () => {
        setFormKey('registration');
        onOpen();
    };

    return (
        <>
            <ButtonGroup>
                {registration.type === RegistrationTypeEnum.Normal ? (
                    <Button
                        variant="solid"
                        colorScheme="brand"
                        {...props}
                        onClick={openRegisterForm}
                    >
                        <FormattedMessage
                            id="user.onboarding.cta.register"
                            defaultMessage="Kostenlos registrieren"
                        />
                    </Button>
                ) : null}

                <Button
                    borderWidth="2px"
                    variant="outline"
                    colorScheme="gray"
                    color="white"
                    _hover={{ color: 'white', bg: 'highlight' }}
                    leftIcon={<FaUser />}
                    {...props}
                    onClick={openLoginForm}
                >
                    <FormattedMessage id="user.onboarding.cta.login" defaultMessage="Anmelden" />
                </Button>
            </ButtonGroup>

            {formKey ? (
                <UserOnboardingModal
                    event={event}
                    isOpen={isOpen}
                    onClose={onModalClose}
                    initialForm={formKey}
                />
            ) : null}
        </>
    );
};

export { UserOnboardingModal };

export default UserOnboarding;
